import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import CaseStudiesDialog from "@/components/CaseStudiesDialog";
import {
  ArrowLeft,
  CheckCircle,
  Zap,
  Users,
  Target,
  TrendingUp,
} from "lucide-react";
import {
  Heart,
  CreditCard,
  ShoppingCart,
  Store,
  Phone,
  Radio,
  Zap as Energy,
  Car,
  GraduationCap,
  Home,
  Factory,
  Truck,
  Clapperboard,
  Package,
  Power,
} from "lucide-react";

// Import industry images
import healthcareImg from "@/assets/healthcare-industry.jpg";
import fintechImg from "@/assets/fintech-industry.jpg";
import ecommerceImg from "@/assets/ecommerce-industry.jpg";
import retailImg from "@/assets/retail-industry.jpg";
import telecomImg from "@/assets/telecom-industry.jpg";
import mediaImg from "@/assets/media-industry.jpg";
import energyImg from "@/assets/energy-industry.jpg";
import automotiveImg from "@/assets/automotive-industry.jpg";
import educationImg from "@/assets/education-industry.jpg";
import realEstateImg from "@/assets/real-estate-industry.jpg";
import manufacturingImg from "@/assets/manufacturing-industry.jpg";
import logisticsImg from "@/assets/logistics-industry.jpg";
import entertainmentImg from "@/assets/entertainment-industry.jpg";
import utilitiesImg from "@/assets/utilities-industry.jpg";
import consumerGoodsImg from "@/assets/consumer-goods-industry.jpg";

import healthcareOverviewImg from "@/assets/Industries/Healthcare-Overview.jpg";
import healthcareChallengesImg from "@/assets/Industries/Healthcare-Overview.jpg";
import healthcareTechImg from "@/assets/Industries/Healthcare-TechImage.jpg";
import healthcareBenefitsImg from "@/assets/Industries/Healthcare-Benefits.jpg";
import healthcareCaseStudyImg from "@/assets/Industries/Healthcare-CaseStudy.jpg";

import mfgAutoOverviewImg from "@/assets/Industries/mfgAuto-Overview.jpg";
import mfgAutoChallengesImg from "@/assets/Industries/Healthcare-Overview.jpg";
import mfgAutoTechImg from "@/assets/Industries/mfgAuto-TechImage.jpg";
import mfgAutoBenefitsImg from "@/assets/Industries/mfgAuto-Benefits.jpg";
import mfgAutoCaseStudyImg from "@/assets/Industries/mfgAuto-CaseStudy.jpg";

import logisticsOverviewImg from "@/assets/Industries/logistics-overview.jpg";
import logisticsChallengesImg from "@/assets/logistics-industry.jpg";
import logisticsTechImg from "@/assets/Industries/logistics-Tech.jpg";
import logisticsBenefitsImg from "@/assets/Industries/logistics-benefits.jpg";
import logisticsCaseStudyImg from "@/assets/Industries/logistics-caseStudy.jpg";

import utilitiesOverviewImg from "@/assets/Industries/utilities-overview.jpg";
import utilitiesChallengesImg from "@/assets/logistics-industry.jpg";
import utilitiesTechImg from "@/assets/Industries/utilities-tech.jpg";
import utilitiesBenefitsImg from "@/assets/Industries/utilitites-benefits.jpg";
import utilitiesCaseStudyImg from "@/assets/Industries/utilities-caseStudy.jpg";

import educationOverviewImg from "@/assets/Industries/edu_privacy_community.svg";
import educationChallengesImg from "@/assets/logistics-industry.jpg";
import educationTechImg from "@/assets/Industries/edu_competition_trophy.svg";
import educationBenefitsImg from "@/assets/Industries/edu_mind_garden.svg";
import educationCaseStudyImg from "@/assets/Industries/edu_talent_showcase.svg";

import bfsiOverviewImg from "@/assets/Industries/bfsi_analytics_insights.svg";
import bfsiChallengesImg from "@/assets/logistics-industry.jpg";
import bfsiTechImg from "@/assets/Industries/bfsi_disaster_recovery.svg";
import bfsiBenefitsImg from "@/assets/Industries/bfsi_realtime_transactions.svg";
import bfsiCaseStudyImg from "@/assets/Industries/bfsi_vault_security.svg";

import commerceOverviewImg from "@/assets/Industries/Commerce-OverviewImg.jpg";
import commerceChallengesImg from "@/assets/logistics-industry.jpg";
import commerceTechImg from "@/assets/Industries/commerce-TechImg.jpg";
import commerceBenefitsImg from "@/assets/Industries/commerce-BenefitsImg.jpg";
import commerceCaseStudyImg from "@/assets/Industries/commerce-CaseStudy.jpg";

interface IndustrySegment {
  title: string;

  challenges: string[];
  solutions: string[];
  technologies: string[];
  benefits: string[];
}

interface IndustrySegment {
  title: string;

  challenges: string[];
  solutions: string[];
  technologies: string[];
  benefits: string[];
}

interface IndustryData {
  title: string;
  description: string;
  image: string;
  overview: string;

  sectionImages?: {
    overview?: string;
    challenges?: string;
    technologies?: string;
    benefits?: string;
    caseStudies?: string;
  };

  // OPTIONAL because segmented industries may not use root-level arrays
  challenges?: string[];
  solutions?: string[];
  technologies?: string[];
  benefits?: string[];

  // OPTIONAL segmented structure
  segments?: IndustrySegment[];

  caseStudies: {
    title: string;
    description: string;
    results: string[];
  }[];

  stats: {
    label: string;
    value: string;
  }[];
}

const industriesData: { [key: string]: IndustryData } = {
  healthcare: {
    title: "Healthcare",
    description:
      "Transforming healthcare through digital innovation with AI-driven healthcare platforms, telemedicine solutions, IoT-enabled diagnostics, and integrated care ecosystems for modern healthcare organizations.",
    image: healthcareImg,

    sectionImages: {
      overview: healthcareOverviewImg,
      // challenges: logisticsChallengesImg,
      technologies: healthcareTechImg,
      benefits: healthcareBenefitsImg,
      caseStudies: healthcareCaseStudyImg,
    },

    overview:
      "Wrizto delivers advanced healthcare technology platforms designed to simplify care delivery, improve patient engagement, and enable connected healthcare ecosystems. With expertise in healthcare IT modernization, digital transformation, telemedicine, EMR integration, IoT healthcare connectivity, and analytics, we empower healthcare providers to achieve operational excellence, enhanced patient experiences, and improved clinical outcomes.",

    challenges: [
      "Fragmented healthcare systems and interoperability gaps",
      "Complex healthcare compliance and regulatory requirements",
      "Limited patient engagement and care accessibility",
      "Inefficient clinical workflows and operational bottlenecks",
      "Legacy EMR/EHR modernization challenges",
      "Remote monitoring and connected care limitations",
      "Scalability for growing patient populations",
    ],

    solutions: [
      // "Digital Health Platform Development",
      "Custom Healthcare Platforms",
      // "Patient Engagement Systems",
      "Health Data Management",
      // "Healthcare Workflow Automation",
      // "Telemedicine & Virtual Care Solutions",
      // "Doctor-Patient Communication Systems",
      "EMR / EHR Integration",
      "Healthcare Interoperability",
      // "Clinical Workflow Integration",
      "IoT & Point of Care Device Connectivity",
      "Remote Patient Monitoring",
      "Healthcare Analytics & Predictive Insights",
      // "Healthcare Commerce Solutions",
      "Online Pharmacy & Insurance Integration",
    ],

    technologies: [
      "Custom Development",
      "AI-based Healthcare Solutions",
      "HL7 FHIR",
      "DICOM",
      "EMR/EHR Management",
      "Epic Integration",
      "Cerner",
      "IoT Device Connectivity",
      "Remote Monitoring Sensors",
      "Telemedicine Integration",
      "Health Wallet",
      "Population Health Management",
      "Appointment Scheduling",
      "Real-time Healthcare Analytics",
      "HIPAA Compliance",
      "Secure Cloud Infrastructure",
      "Blockchain",
      "AI/ML",
      "Mobile Healthcare Applications",
    ],

    benefits: [
      "Improved patient care delivery and clinical outcomes",
      "Enhanced patient engagement and connected care ecosystems",
      "Streamlined healthcare workflows and operational efficiency",
      "Secure healthcare data interoperability",
      "Reduced care delivery costs through automation",
      "Expanded access through telemedicine and virtual care",
      "Scalable healthcare commerce and digital ecosystems",
    ],

    caseStudies: [
      {
        title:
          "Integrated Digital Healthcare Ecosystem for Multi-Specialty Hospital Group",
        description:
          "Developed AI-powered healthcare platform integrating EMR, telemedicine, patient engagement, and analytics across multiple facilities.",
        results: [
          "45% improvement in operational efficiency",
          "35% increase in patient engagement",
          "50% faster care coordination",
        ],
      },
      {
        title: "Remote Patient Monitoring & Telemedicine Platform",
        description:
          "Built IoT-enabled telemedicine and remote diagnostics platform for large-scale healthcare access expansion.",
        results: [
          "400% increase in remote care access",
          "65% reduction in avoidable hospital visits",
          "96% patient satisfaction rate",
        ],
      },
    ],

    stats: [
      { label: "Healthcare Platforms Delivered", value: "6+" },
      { label: "Connected Patient Ecosystems", value: "5M+" },
      { label: "Compliance Coverage", value: "100%" },
      { label: "Operational Efficiency Gain", value: "45%" },
    ],
  },
  fintech: {
    title: "FinTech & BFSI",

    description:
      "Modernize financial ecosystems with advanced FinTech innovation and enterprise BFSI transformation — spanning digital payments, event-driven banking, insurance, compliance, lending, and resilient financial infrastructure.",

    image: fintechImg,

    sectionImages: {
      overview: bfsiOverviewImg,
      technologies: bfsiTechImg,
      benefits: bfsiBenefitsImg,
      caseStudies: bfsiCaseStudyImg,
    },

    overview:
      "Our FinTech & BFSI practice helps financial institutions, digital banks, NBFCs, insurers, and payment providers transform legacy infrastructure into scalable, secure, event-driven ecosystems. We combine modern FinTech innovation with BFSI-grade operational resilience to enable zero-disruption modernization, real-time transactions, regulatory compliance, and future-ready financial operations.",

    segments: [
      {
        title: "FinTech",

        challenges: [
          "Legacy payment ecosystem modernization",
          "Digital lending and customer onboarding inefficiencies",
          "Fragmented financial transaction observability",
          "Scalable API-first innovation challenges",
        ],

        solutions: [
          "Digital Payment & Wallet Platforms",
          "Lending & Credit Automation Systems",
          "Real-time Event-Driven Financial Platforms",
          "Open API Financial Ecosystems",
        ],

        technologies: [
          "Custom Development",
          "Kafka",
          "Kraft",
          "Microservices",
          "Open Banking APIs",
          "AI/ML",
          "Cloud Infrastructure",
          "ElasticSearch",
        ],

        benefits: [
          "Faster digital financial innovation",
          "Improved customer onboarding",
          "Real-time transaction scalability",
          "Enhanced product agility",
        ],
      },

      {
        title: "BFSI",

        challenges: [
          "Decade-old monolithic banking systems",
          "Single-point operational failures",
          "Manual disaster recovery risks",
          "Regulatory compliance and audit complexity",
        ],

        solutions: [
          "Banking Monolith to Microservices Transformation",
          "DC/DR Automation with MirrorMaker 2",
          "ElasticSearch Tiered Financial Storage",
          "Regulatory Audit & Operational Resilience Platforms",
        ],

        technologies: [
          "Custom Development",
          "MirrorMaker 2",
          "DC/DR",
          "ElasticSearch",
          "Compliance Systems",
          "Security Frameworks",
          "Open-source Core Infrastructure",
        ],

        benefits: [
          "Zero-disruption modernization",
          "Near-zero downtime resilience",
          "Lower infrastructure costs",
          "Improved auditability and compliance",
        ],
      },
    ],

    caseStudies: [
      {
        title: "Banking Core Modernization",
        description:
          "Transformed a 10-year legacy monolithic banking system into a Kafka-driven microservices ecosystem without disrupting live financial operations.",
        results: [
          "5+ independently deployable microservices",
          "Near-zero downtime during migration",
          "Automated DC/DR failover",
        ],
      },
      {
        title: "ElasticSearch Tiered Financial Data Platform",
        description:
          "Implemented hot/warm/cold ElasticSearch storage architecture for scalable transaction search, analytics, and cost optimization.",
        results: [
          "3-tier lifecycle storage model",
          "Lower long-term infrastructure cost",
          "Full audit + searchability retained",
        ],
      },
    ],

    stats: [
      { label: "Independent Services", value: "5+" },
      { label: "Downtime During Migration", value: "~0" },
      { label: "Storage Architecture", value: "3-tier" },
      { label: "DC/DR Resilience", value: "Automated" },
    ],
  },
  // ecommerce: {
  //   // icon: ShoppingCart,
  //   title: "E-commerce",
  //   // category: "Digital Commerce",
  //   description: "Create compelling online shopping experiences with robust e-commerce platforms and solutions.",
  //   image: ecommerceImg,
  //   overview: "Our e-commerce solutions drive online business growth through scalable platforms, seamless user experiences, and powerful analytics. We help businesses of all sizes establish and expand their digital presence with cutting-edge technology.",
  //   challenges: [
  //     "Managing high traffic volumes during peak seasons",
  //     "Integration with multiple payment gateways",
  //     "Inventory management across multiple channels",
  //     "Mobile-first customer experience",
  //     "Personalization and recommendation engines"
  //   ],
  //   solutions: [
  //     "Custom E-commerce Platforms",
  //     "Multi-vendor Marketplaces",
  //     "Mobile Commerce Applications",
  //     "Payment Gateway Integration",
  //     "Inventory Management Systems",
  //     "Customer Analytics and Personalization"
  //   ],
  //   technologies: ["Shopify Plus", "WooCommerce", "Magento", "Stripe", "PayPal", "AWS", "CDN", "Elasticsearch"],
  //   benefits: [
  //     "Increased online sales by 150%",
  //     "Improved customer retention rates",
  //     "Reduced cart abandonment by 35%",
  //     "Enhanced mobile shopping experience",
  //     "Better inventory turnover"
  //   ],
  //   caseStudies: [
  //     {
  //       title: "Multi-vendor Marketplace Platform",
  //       description: "Built scalable marketplace supporting 1000+ vendors",
  //       results: ["300% increase in GMV", "50% improvement in vendor satisfaction", "99.9% platform uptime"]
  //     },
  //     {
  //       title: "Mobile-First Fashion Retailer",
  //       description: "Developed PWA for leading fashion brand",
  //       results: ["200% mobile conversion increase", "40% faster page load times", "60% increase in mobile traffic"]
  //     }
  //   ],
  //   stats: [
  //     { label: "E-commerce Projects", value: "10+" },
  //     { label: "Conversion Rate Increase", value: "150%" },
  //     { label: "Mobile Traffic", value: "70%" }
  //   ]
  // },
  // retail: {
  //   // icon: Store,
  //   title: "Retail",
  //   // category: "Retail & Consumer",
  //   description: "Modernize retail operations with omnichannel solutions and customer engagement platforms.",
  //   image: retailImg,
  //   overview: "We help retailers create seamless omnichannel experiences that bridge online and offline touchpoints. Our solutions enhance customer engagement, optimize operations, and drive sales growth through innovative technology.",
  //   challenges: [
  //     "Creating unified omnichannel experiences",
  //     "Real-time inventory synchronization",
  //     "Customer data integration across touchpoints",
  //     "Staff training and technology adoption",
  //     "Supply chain optimization"
  //   ],
  //   solutions: [
  //     "Point of Sale (POS) Systems",
  //     "Omnichannel Customer Experience",
  //     "Inventory Management Solutions",
  //     "Customer Loyalty Programs",
  //     "Staff Management Systems",
  //     "Supply Chain Optimization"
  //   ],
  //   technologies: ["Salesforce Commerce", "Oracle Retail", "SAP Retail", "Microsoft Dynamics", "Square", "RFID", "IoT"],
  //   benefits: [
  //     "Unified customer experience across channels",
  //     "30% improvement in inventory turnover",
  //     "Increased customer loyalty and retention",
  //     "Enhanced staff productivity",
  //     "Better demand forecasting"
  //   ],
  //   caseStudies: [
  //     {
  //       title: "National Retail Chain Omnichannel",
  //       description: "Implemented omnichannel solution for 80+ store chain",
  //       results: ["25% increase in cross-channel sales", "50% improvement in inventory accuracy", "20% higher customer satisfaction"]
  //     },
  //     {
  //       title: "Luxury Brand Digital Transformation",
  //       description: "Modernized retail operations for premium fashion brand",
  //       results: ["40% increase in VIP customer engagement", "35% improvement in staff efficiency", "60% growth in online-to-store visits"]
  //     }
  //   ],
  //   stats: [
  //     { label: "Retail Locations", value: "10K+" },
  //     { label: "Transactions Daily", value: "2M+" },
  //     { label: "Inventory Accuracy", value: "99.5%" },
  //     { label: "Customer Satisfaction", value: "4.7/5" }
  //   ]
  // },
  commerce: {
    // icon: ShoppingCart,
    title: "E-commerce & Retail",
    // category: "Digital Commerce & Retail",
    description:
      "Transforming retail and commerce through smart digital solutions that empower retailers, distributors, vendors, and customers with intelligent e-commerce platforms, supply chain technology, procurement systems, and seamless fulfillment ecosystems.",
    image: ecommerceImg,

    sectionImages: {
      overview: commerceOverviewImg,
      // challenges: logisticsChallengesImg,
      technologies: commerceTechImg,
      benefits: commerceBenefitsImg,
      caseStudies: commerceCaseStudyImg,
    },

    overview:
      "Modern retail businesses require connected digital commerce ecosystems that unify e-commerce, retail distribution, procurement, inventory, logistics, and customer engagement. Our smart retail platform modernizes rural and urban commerce through scalable digital infrastructure that enables omnichannel growth, improves operational efficiency, strengthens dealer and retailer networks, and delivers exceptional customer experiences.",

    segments: [
      {
        title: "E-commerce",
        challenges: [
          "Disconnected online marketplaces",
          "Poor digital customer journey",
          "Cart abandonment and low conversion",
          "Inefficient payment and fulfillment systems",
        ],

        solutions: [
          "B2C E-commerce Platforms",
          "Hyperlocal Commerce Solutions",
          "Omnichannel Digital Storefronts",
          "Payment Gateway & Fulfillment Automation",
        ],

        technologies: [
          "Custom Development",
          "Shopify Plus",
          "Magento",
          "WooCommerce",
          "Stripe",
          "PayPal",
          "Mobile Commerce",
          "AI/ML",
        ],

        benefits: [
          "Higher online conversion rates",
          "Faster digital order fulfillment",
          "Improved customer experience",
          "Scalable online growth",
        ],
      },

      {
        title: "Retail",
        challenges: [
          "Inefficient procurement and vendor management",
          "Inventory inconsistency across locations",
          "Limited distributor visibility",
          "Rural retail digitization barriers",
        ],

        solutions: [
          "B2B Distributor Ordering Systems",
          "Smart Procurement Systems",
          "Inventory Management Platforms",
          "Rural Commerce Digitization",
        ],

        technologies: [
          "Custom Development",
          "SAP Retail",
          "Oracle Retail",
          "Microsoft Dynamics",
          "RFID",
          "IoT",
          "Geo-location Systems",
          "Cloud Infrastructure",
        ],

        benefits: [
          "Optimized procurement efficiency",
          "Better inventory visibility",
          "Expanded rural reach",
          "Stronger dealer-retailer collaboration",
        ],
      },
    ],

    caseStudies: [
      {
        title: "Omnichannel Retail & Distribution Transformation",
        description:
          "Built a connected retail ecosystem for a multi-region commerce enterprise integrating e-commerce, dealer networks, procurement, and fulfillment",
        results: [
          "250% increase in digital order volume",
          "40% faster procurement cycle",
          "60% improvement in retailer onboarding",
          "35% increase in fulfillment efficiency",
        ],
      },
      {
        title: "Rural Commerce Enablement Platform",
        description:
          "Developed a scalable rural retail digitization and assisted commerce platform connecting local retailers, distributors, and consumers",
        results: [
          "5000+ rural retailers digitized",
          "70% faster inventory replenishment",
          "45% growth in regional distribution",
          "90% supply chain visibility",
        ],
      },
    ],

    stats: [
      { label: "Commerce Projects", value: "25+" },
      { label: "Retailers & Dealers Enabled", value: "50K+" },
      { label: "Daily Transactions", value: "3M+" },
      { label: "Fulfillment Efficiency", value: "40%" },
      { label: "Inventory Visibility", value: "99%" },
      { label: "Customer Satisfaction", value: "4.8/5" },
    ],
  },
  // telecom: {
  //   // icon: Phone,
  //   title: "Telecommunications",
  //   // category: "Telecom & Networking",
  //   description:
  //     "Enhance telecommunications infrastructure with cutting-edge network and customer management solutions.",
  //   image: telecomImg,
  //   overview:
  //     "Our telecommunications solutions enable service providers to deliver next-generation services while optimizing network performance and customer experience. We specialize in 5G, IoT, and network automation technologies.",
  //   challenges: [
  //     "5G network deployment and optimization",
  //     "Legacy system modernization",
  //     "Network security and reliability",
  //     "Customer service automation",
  //     "IoT device management at scale",
  //   ],
  //   solutions: [
  //     "5G Network Infrastructure",
  //     "Network Management Systems",
  //     "Customer Service Platforms",
  //     "Billing and Revenue Management",
  //     "IoT Device Management",
  //     "Network Security Solutions",
  //   ],
  //   technologies: [
  //     "5G",
  //     "SDN/NFV",
  //     "OSS/BSS",
  //     "VoIP",
  //     "SIP",
  //     "Network Orchestration",
  //     "Edge Computing",
  //     "AI/ML",
  //   ],
  //   benefits: [
  //     "Improved network performance and reliability",
  //     "50% reduction in operational costs",
  //     "Enhanced customer experience",
  //     "Faster service deployment",
  //     "Better network security",
  //   ],
  //   caseStudies: [
  //     {
  //       title: "5G Network Rollout",
  //       description: "Supported nationwide 5G infrastructure deployment",
  //       results: [
  //         "99.9% network uptime",
  //         "40% faster data speeds",
  //         "30% reduction in latency",
  //       ],
  //     },
  //     {
  //       title: "IoT Platform for Smart Cities",
  //       description: "Developed IoT management platform for urban services",
  //       results: [
  //         "1M+ connected devices",
  //         "25% energy savings",
  //         "50% faster incident response",
  //       ],
  //     },
  //   ],
  //   stats: [
  //     { label: "Network Nodes", value: "50K+" },
  //     { label: "Connected Devices", value: "100M+" },
  //     { label: "Uptime", value: "99.99%" },
  //     { label: "Cost Reduction", value: "45%" },
  //   ],
  // },
  // media: {
  //   // icon: Radio,
  //   title: "Media & Entertainment",
  //   // category: "Media & Entertainment",
  //   description:
  //     "Deliver engaging digital media experiences with content management and distribution platforms.",
  //   image: mediaImg,
  //   overview:
  //     "We create innovative media and entertainment solutions that captivate audiences and drive engagement. Our platforms support content creation, distribution, and monetization across multiple channels and devices.",
  //   challenges: [
  //     "Content delivery at global scale",
  //     "Multi-platform content distribution",
  //     "Digital rights management",
  //     "Audience engagement and retention",
  //     "Monetization optimization",
  //   ],
  //   solutions: [
  //     "Content Management Systems",
  //     "Video Streaming Platforms",
  //     "Digital Publishing Solutions",
  //     "Audience Analytics",
  //     "Content Distribution Networks",
  //     "Monetization Platforms",
  //   ],
  //   technologies: [
  //     "CDN",
  //     "Video Encoding",
  //     "DRM",
  //     "CMS",
  //     "Adobe Creative Suite",
  //     "AWS Media Services",
  //     "AI/ML",
  //   ],
  //   benefits: [
  //     "Global content reach and accessibility",
  //     "60% improvement in viewer engagement",
  //     "Enhanced content security",
  //     "Better audience insights",
  //     "Increased revenue streams",
  //   ],
  //   caseStudies: [
  //     {
  //       title: "Global Streaming Platform",
  //       description:
  //         "Built scalable streaming service for international broadcaster",
  //       results: [
  //         "10M+ concurrent viewers",
  //         "99.9% stream reliability",
  //         "40% increase in subscriber retention",
  //       ],
  //     },
  //     {
  //       title: "Digital Publishing Revolution",
  //       description: "Modernized content management for major media house",
  //       results: [
  //         "300% faster content publishing",
  //         "50% increase in reader engagement",
  //         "35% growth in digital subscriptions",
  //       ],
  //     },
  //   ],
  //   stats: [
  //     { label: "Content Streams", value: "1B+" },
  //     { label: "Global Reach", value: "150+ Countries" },
  //     { label: "Uptime", value: "99.95%" },
  //     { label: "Engagement Increase", value: "60%" },
  //   ],
  // },
  telecomMedia: {
    // icon: Radio,
    title: "Telecom & Media",
    // category: "Telecommunications & Media",
    description:
      "Empower telecommunications and media enterprises with advanced network infrastructure, digital content platforms, streaming technologies, and customer engagement solutions.",

    image: telecomImg, // Replace with combined telecom/media image if available

    overview:
      "Our Telecom & Media solutions help organizations modernize communication networks, optimize customer experiences, and deliver engaging digital content at global scale. We combine expertise in 5G, IoT, network automation, streaming platforms, and digital media ecosystems to support next-generation connectivity and content delivery.",

    challenges: [
      "5G deployment and network optimization",
      "Legacy telecom system modernization",
      "Network reliability and cybersecurity",
      "Global-scale content delivery",
      "Multi-platform content distribution",
      "Digital rights management and content security",
      "Audience engagement and retention",
      "IoT device management at scale",
    ],

    solutions: [
      "5G Network Infrastructure",
      "Network Management Systems",
      "OSS/BSS Platforms",
      "Customer Service & Billing Systems",
      "IoT Device Management",
      "Content Management Systems",
      "Video Streaming Platforms",
      "Digital Publishing Solutions",
      "Content Distribution Networks",
      "Audience Analytics & Monetization Platforms",
    ],

    technologies: [
      "Custom Development",
      "5G",
      "SDN/NFV",
      "OSS/BSS",
      "VoIP",
      "SIP",
      "Network Orchestration",
      "Edge Computing",
      "CDN",
      "Video Encoding",
      "DRM",
      "CMS",
      "Adobe Creative Suite",
      "AWS Media Services",
      "AI/ML",
    ],

    benefits: [
      "Improved network performance and reliability",
      "Enhanced customer and viewer experiences",
      "Faster service and content delivery",
      "Global-scale content accessibility",
      "Better audience engagement and retention",
      "Enhanced content security and rights management",
      "Reduced operational costs",
      "Increased revenue and monetization opportunities",
    ],

    caseStudies: [
      {
        title: "5G & Smart Connectivity Platform",
        description:
          "Supported nationwide 5G deployment and IoT infrastructure modernization for telecom providers and smart city ecosystems",
        results: [
          "99.99% network uptime",
          "40% faster data speeds",
          "1M+ connected IoT devices",
          "50% faster incident response",
        ],
      },
      {
        title: "Global Streaming & Digital Media Platform",
        description:
          "Built scalable streaming and digital publishing ecosystem for international media enterprise",
        results: [
          "10M+ concurrent viewers",
          "99.9% streaming reliability",
          "60% improvement in audience engagement",
          "35% growth in digital subscriptions",
        ],
      },
    ],

    stats: [
      { label: "Connected Devices", value: "100M+" },
      { label: "Network Nodes", value: "50K+" },
      { label: "Global Reach", value: "150+ Countries" },
      { label: "Platform Uptime", value: "99.99%" },
      { label: "Engagement Increase", value: "60%" },
    ],
  },

  education: {
    title: "Education / EdTech",
    description:
      "Build privacy-first educational ecosystems that combine talent development, safe digital communities, structured competitions, and cognitive growth for under-18 users.",

    image: educationImg,

    sectionImages: {
      overview: educationOverviewImg,
      // challenges: logisticsChallengesImg,
      technologies: educationTechImg,
      benefits: educationBenefitsImg,
      caseStudies: educationCaseStudyImg,
    },

    overview:
      "We design next-generation EdTech platforms that move beyond traditional LMS systems into child-safe digital ecosystems. Our expertise includes Flutter-based mobile applications, under-18 privacy architecture, parental consent systems, anonymous social engagement, structured competition engines, and developmental modules like Mind Garden — enabling children to safely showcase talent, build confidence, and grow within protected digital environments.",

    challenges: [
      "Lack of safe digital platforms exclusively for under-18 users",
      "Exposure of children to unsafe public social platforms",
      "No parental oversight or consent enforcement",
      "Absence of privacy-first identity protection",
      "Unstructured competitions with no scalable engagement model",
      "Limited focus on long-term developmental growth beyond content sharing",
    ],

    solutions: [
      "Child-Safe Talent Showcase Platforms",
      "Parental Consent & Approval Systems",
      "Anonymous Community + Follow Ecosystems",
      "Competition Engines with Automated Scoring",
      "Mind Garden Cognitive Development Modules",
      "Cross-Platform Flutter Mobile Apps",
    ],

    technologies: [
      "Custom Development",
      "Flutter",
      "Python",
      "Android",
      "iOS",
      "Privacy-first APIs",
      "Competition Engine",
      "Parent Verification",
      "Cognitive Gaming",
    ],

    benefits: [
      "Zero public exposure of personal information",
      "Safe under-18-only digital ecosystem",
      "Parent-controlled onboarding and governance",
      "Single codebase for Android + iOS scalability",
      "Structured growth through talent + competition + cognitive development",
      "Community building without compromising child identity",
    ],

    caseStudies: [
      {
        title: "Under-18 Talent Ecosystem",
        description:
          "Built a privacy-first Flutter mobile platform for children to showcase talent, engage socially, and compete safely under verified parental governance.",
        results: [
          "U18-exclusive platform architecture",
          "Zero public PII exposure",
          "Android + iOS deployment",
        ],
      },
      {
        title: "Mind Garden Cognitive Development Platform",
        description:
          "Developed an integrated cognitive gaming and developmental tracking ecosystem to support ongoing child growth beyond content consumption.",
        results: [
          "5 core platform modules",
          "Integrated cognitive development",
          "Long-term child progress visibility",
        ],
      },
    ],

    stats: [
      { label: "Target Audience", value: "U18 Only" },
      { label: "Public Personal Info Exposure", value: "0" },
      { label: "Platforms Supported", value: "Android + iOS" },
      { label: "Core Modules", value: "5" },
    ],
  },
  energyUtilities: {
    title: "Energy & Utilities Solutions",

    description:
      "Drive sustainable energy innovation and utility modernization through intelligent digital ecosystems spanning smart grids, renewable platforms, operational automation, and infrastructure resilience.",

    image: energyImg,

    sectionImages: {
      overview: utilitiesOverviewImg,
      technologies: utilitiesTechImg,
      benefits: utilitiesBenefitsImg,
      caseStudies: utilitiesCaseStudyImg,
    },

    overview:
      "Our Energy & Utilities practice helps organizations modernize energy generation, renewable systems, utility operations, and infrastructure ecosystems through scalable digital transformation, industrial intelligence, predictive analytics, and operational automation.",

    segments: [
      {
        title: "Energy",

        challenges: [
          "Renewable energy integration complexity",
          "Demand forecasting inefficiency",
          "Energy asset performance optimization",
          "Real-time generation intelligence gaps",
        ],

        solutions: [
          "Renewable Energy Monitoring Platforms",
          "Demand Forecasting Systems",
          "Energy Analytics & Reporting",
          "IoT-based Energy Monitoring",
        ],

        technologies: [
          "Custom Development",
          "Smart Meters",
          "AI/ML",
          "Grid Analytics",
          "IoT Sensors",
          "Predictive Analytics",
          "Cloud Platforms",
        ],

        benefits: [
          "Improved renewable performance",
          "Better forecasting accuracy",
          "Higher energy efficiency",
          "Optimized generation intelligence",
        ],
      },

      {
        title: "Utilities",

        challenges: [
          "Aging utility infrastructure",
          "Grid reliability and resilience",
          "Remote operations limitations",
          "Predictive maintenance complexity",
        ],

        solutions: [
          "SCADA Integration",
          "OPC UA Connectivity",
          "Remote Utility Operations",
          "Asset Lifecycle Management",
        ],

        technologies: [
          "Custom Development",
          "SCADA",
          "OPC UA",
          "PLC",
          "GIS Mapping",
          "Industrial Automation",
          "Remote Monitoring",
        ],

        benefits: [
          "Improved grid reliability",
          "Reduced downtime",
          "Better asset lifecycle management",
          "Enhanced remote operational control",
        ],
      },
    ],

    caseStudies: [
      {
        title: "Smart Grid & Utility Infrastructure Modernization",
        description:
          "Implemented intelligent smart grid, remote operations, and predictive asset management systems.",
        results: [
          "45% reduction in outage duration",
          "35% operational efficiency improvement",
          "30% reduction in maintenance costs",
          "99.9% grid reliability",
        ],
      },
      {
        title: "Renewable Energy Analytics Platform",
        description:
          "Developed renewable energy intelligence platform with solar, wind, IoT monitoring, and predictive maintenance.",
        results: [
          "50% increase in renewable asset efficiency",
          "40% faster issue detection",
          "25% lower downtime",
          "95% forecasting accuracy",
        ],
      },
    ],

    stats: [
      { label: "Utility Providers Served", value: "25+" },
      { label: "Smart Assets Managed", value: "10M+" },
      { label: "Grid Reliability", value: "99.9%" },
      { label: "Operational Efficiency Gain", value: "35%" },
      { label: "Maintenance Cost Reduction", value: "30%" },
      { label: "Customer Satisfaction", value: "4.7/5" },
    ],
  },
  // energy: {
  //   // icon: Energy,
  //   title: "Energy",
  //   // category: "Energy & Utilities",
  //   description:
  //     "Optimize energy operations with smart grid solutions and renewable energy management systems.",
  //   image: energyImg,
  //   overview:
  //     "We deliver innovative energy and utilities solutions that optimize operations, enhance sustainability, and improve customer service. Our expertise spans smart grid management, renewable energy integration, and regulatory compliance.",
  //   challenges: [
  //     "Aging infrastructure and modernization needs",
  //     "Integration of renewable energy sources",
  //     "Grid stability and reliability",
  //     "Regulatory compliance and reporting",
  //     "Customer demand for smart energy solutions",
  //   ],
  //   solutions: [
  //     "Smart Grid Management Systems",
  //     "Renewable Energy Integration",
  //     "Energy Trading Platforms",
  //     "Asset Management Solutions",
  //     "Customer Energy Portals",
  //     "Predictive Maintenance Systems",
  //   ],
  //   technologies: [
  //     "SCADA",
  //     "IoT Sensors",
  //     "Blockchain",
  //     "AI/ML",
  //     "GIS",
  //     "Meter Data Management",
  //     "Smart Meters",
  //   ],
  //   benefits: [
  //     "Improved grid reliability and efficiency",
  //     "30% reduction in operational costs",
  //     "Enhanced renewable energy integration",
  //     "Better demand forecasting and management",
  //     "Improved customer satisfaction",
  //   ],
  //   caseStudies: [
  //     {
  //       title: "Smart Grid Implementation",
  //       description:
  //         "Deployed smart grid solution for regional utility serving 2M customers",
  //       results: [
  //         "25% reduction in outages",
  //         "40% improvement in energy efficiency",
  //         "99.9% grid reliability",
  //       ],
  //     },
  //     {
  //       title: "Renewable Energy Management",
  //       description:
  //         "Built integrated platform for wind and solar energy management",
  //       results: [
  //         "50% increase in renewable capacity",
  //         "30% reduction in carbon footprint",
  //         "95% forecast accuracy",
  //       ],
  //     },
  //   ],
  //   stats: [
  //     { label: "Energy Projects", value: "4+" },
  //     { label: "Smart Meters Managed", value: "10M+" },
  //     { label: "Grid Reliability", value: "99.9%" },
  //     { label: "Cost Savings", value: "30%" },
  //   ],
  // },
  // automotive: {
  //   // icon: Car,
  //   title: "Automotive",
  //   // category: "Automotive & Transportation",
  //   description:
  //     "Drive innovation in automotive industry with connected vehicle solutions and manufacturing systems.",
  //   image: automotiveImg,
  //   overview:
  //     "Our automotive solutions encompass connected vehicles, smart manufacturing, and digital transformation initiatives. We help automotive companies innovate with IoT, AI, and advanced manufacturing technologies.",
  //   challenges: [
  //     "Connected vehicle technology integration",
  //     "Autonomous driving development",
  //     "Supply chain complexity",
  //     "Manufacturing process optimization",
  //     "Customer experience digitization",
  //   ],
  //   solutions: [
  //     "Connected Vehicle Platforms",
  //     "Fleet Management Systems",
  //     "Manufacturing Execution Systems",
  //     "Dealer Management Solutions",
  //     "Telematics and Diagnostics",
  //     "Customer Experience Platforms",
  //   ],
  //   technologies: [
  //     "IoT",
  //     "V2X Communication",
  //     "AUTOSAR",
  //     "CAN Bus",
  //     "OBD-II",
  //     "Telematics",
  //     "AI/ML",
  //     "Edge Computing",
  //   ],
  //   benefits: [
  //     "Enhanced vehicle connectivity and safety",
  //     "Improved manufacturing efficiency",
  //     "Better customer experience and service",
  //     "Reduced operational costs",
  //     "Faster time-to-market for new features",
  //   ],
  //   caseStudies: [
  //     {
  //       title: "Connected Vehicle Platform",
  //       description:
  //         "Developed comprehensive connected car solution for major OEM",
  //       results: [
  //         "5M+ connected vehicles",
  //         "40% improvement in diagnostics",
  //         "95% customer satisfaction",
  //       ],
  //     },
  //     {
  //       title: "Smart Manufacturing System",
  //       description: "Implemented Industry 4.0 solution for automotive plant",
  //       results: [
  //         "20% increase in production efficiency",
  //         "50% reduction in defects",
  //         "30% lower maintenance costs",
  //       ],
  //     },
  //   ],
  //   stats: [
  //     { label: "Connected Vehicles", value: "5M+" },
  //     { label: "Manufacturing Plants", value: "50+" },
  //     { label: "Uptime Improvement", value: "25%" },
  //     { label: "Customer Satisfaction", value: "4.8/5" },
  //   ],
  // },
  industrial: {
    title: "Manufacturing & Automotive",

    description:
      "Accelerate automotive innovation and manufacturing excellence with integrated digital engineering solutions across connected mobility, EV ecosystems, Industry 4.0, industrial automation, and enterprise modernization.",

    image: automotiveImg,

    sectionImages: {
      overview: mfgAutoOverviewImg,
      technologies: mfgAutoTechImg,
      benefits: mfgAutoBenefitsImg,
      caseStudies: mfgAutoCaseStudyImg,
    },

    overview:
      "Our Manufacturing & Automotive solutions unify smart mobility engineering with Industry 4.0 transformation — enabling connected vehicles, EV ecosystems, smart factories, industrial IoT, automation, predictive intelligence, and enterprise-scale operational excellence.",

    segments: [
      {
        title: "Automotive",

        challenges: [
          "Connected vehicle ecosystem modernization",
          "EV software and charging infrastructure complexity",
          "Vehicle diagnostics and predictive maintenance",
          "Automotive cybersecurity and compliance",
        ],

        solutions: [
          "Connected Vehicle Platforms",
          "Battery Monitoring Systems",
          "Vehicle Diagnostics & Predictive Maintenance",
          "Embedded Systems & HMI Development",
        ],

        technologies: [
          "Custom Development",
          "Telematics",
          "V2X Communication",
          "AUTOSAR",
          "CAN Bus",
          "OBD-II",
          "Embedded Systems",
          "Cybersecurity",
        ],

        benefits: [
          "Enhanced connected vehicle intelligence",
          "Accelerated EV innovation",
          "Reduced vehicle downtime",
          "Improved mobility ecosystem scalability",
        ],
      },

      {
        title: "Manufacturing",

        challenges: [
          "Legacy manufacturing system integration",
          "Smart factory transformation complexity",
          "Industrial IoT and machine connectivity",
          "Supply chain visibility and production optimization",
        ],

        solutions: [
          "PLC/SCADA & OPC UA Integration",
          "MES Integration",
          "Predictive Maintenance Systems",
          "ERP & Supply Chain Integration",
        ],

        technologies: [
          "Custom Development",
          "PLC",
          "SCADA",
          "OPC UA",
          "MES",
          "ERP",
          "IoT",
          "Edge Computing",
        ],

        benefits: [
          "Improved factory automation",
          "Higher manufacturing productivity",
          "Reduced operational downtime",
          "Enhanced enterprise-wide efficiency",
        ],
      },
    ],

    caseStudies: [
      {
        title: "Connected Vehicle & EV Mobility Transformation",
        description:
          "Built an end-to-end connected mobility ecosystem with telematics, fleet management, battery intelligence, and predictive diagnostics.",
        results: [
          "5M+ connected vehicles",
          "45% faster diagnostics",
          "30% reduction in maintenance costs",
          "50% improvement in fleet visibility",
        ],
      },
      {
        title: "Industry 4.0 Manufacturing Automation Platform",
        description:
          "Delivered smart factory transformation through IoT, PLC/SCADA integration, MES modernization, and predictive maintenance.",
        results: [
          "35% productivity increase",
          "40% downtime reduction",
          "25% lower operational costs",
          "60% faster issue resolution",
        ],
      },
    ],

    stats: [
      { label: "Connected Vehicles", value: "5M+" },
      { label: "Manufacturing Plants", value: "50+" },
      { label: "Fleet Assets Managed", value: "1M+" },
      { label: "Efficiency Improvement", value: "35%" },
      { label: "Downtime Reduction", value: "40%" },
      { label: "Customer Satisfaction", value: "4.8/5" },
    ],
  },
  "real-estate": {
    // icon: Home,
    title: "Real Estate",
    // category: "Real Estate Technology",
    description:
      "Revolutionize property management and real estate transactions with innovative digital solutions.",
    image: realEstateImg,
    overview:
      "Our real estate technology solutions streamline property management, enhance customer experiences, and optimize real estate operations. We help real estate professionals leverage technology for growth and efficiency.",
    challenges: [
      "Property data management and integration",
      "Customer relationship management",
      "Virtual property showcasing",
      "Transaction process automation",
      "Market analysis and reporting",
    ],
    solutions: [
      "Property Management Systems",
      "Real Estate CRM Platforms",
      "Virtual Tour and AR Solutions",
      "Transaction Management Tools",
      "Market Analytics Platforms",
      "Customer Portal Solutions",
    ],
    technologies: [
      "Custom Development",
      "MLS Integration",
      "Zillow API",
      "Property Management Software",
      "VR/AR",
      "Blockchain",
      "AI/ML",
    ],
    benefits: [
      "Streamlined property management processes",
      "Enhanced customer experience",
      "Improved sales and leasing efficiency",
      "Better market insights and analytics",
      "Reduced operational costs",
    ],
    caseStudies: [
      {
        title: "Property Management Platform",
        description: "Built comprehensive platform for 10,000+ properties",
        results: [
          "50% reduction in management time",
          "30% increase in tenant satisfaction",
          "25% improvement in occupancy rates",
        ],
      },
      {
        title: "Real Estate Marketplace",
        description: "Developed online marketplace for property transactions",
        results: [
          "200% increase in property listings",
          "60% faster transaction times",
          "40% growth in user engagement",
        ],
      },
    ],
    stats: [
      { label: "Properties Managed", value: "100K+" },
      { label: "Real Estate Agents", value: "5K+" },
      { label: "Transaction Volume", value: "$10B+" },
      { label: "Customer Satisfaction", value: "4.6/5" },
    ],
  },
  // manufacturing: {
  //   // icon: Factory,
  //   title: "Manufacturing",
  //   // category: "Industrial Manufacturing",
  //   description:
  //     "Optimize manufacturing processes with Industry 4.0 solutions and smart factory technologies.",
  //   image: manufacturingImg,
  //   overview:
  //     "We deliver Industry 4.0 manufacturing solutions that optimize production, improve quality, and reduce costs. Our expertise spans smart factories, predictive maintenance, and digital transformation initiatives.",
  //   challenges: [
  //     "Legacy system modernization",
  //     "Production optimization and efficiency",
  //     "Quality control and assurance",
  //     "Supply chain integration",
  //     "Predictive maintenance implementation",
  //   ],
  //   solutions: [
  //     "Manufacturing Execution Systems (MES)",
  //     "Enterprise Resource Planning (ERP)",
  //     "IoT and Smart Factory Solutions",
  //     "Digital Twin Technology",
  //     "Predictive Maintenance Platforms",
  //     "Quality Management Systems",
  //   ],
  //   technologies: [
  //     "MES",
  //     "ERP",
  //     "IoT",
  //     "Digital Twin",
  //     "AI/ML",
  //     "Robotics",
  //     "SCADA",
  //     "Edge Computing",
  //   ],
  //   benefits: [
  //     "Increased production efficiency",
  //     "Improved product quality",
  //     "Reduced operational costs",
  //     "Enhanced supply chain visibility",
  //     "Better predictive maintenance",
  //   ],
  //   caseStudies: [
  //     {
  //       title: "Smart Factory Implementation",
  //       description:
  //         "Transformed traditional factory into Industry 4.0 facility",
  //       results: [
  //         "35% increase in productivity",
  //         "50% reduction in defects",
  //         "40% lower maintenance costs",
  //       ],
  //     },
  //     {
  //       title: "Digital Twin Deployment",
  //       description:
  //         "Implemented digital twin for complex manufacturing process",
  //       results: [
  //         "25% improvement in efficiency",
  //         "60% faster problem resolution",
  //         "30% reduction in waste",
  //       ],
  //     },
  //   ],
  //   stats: [
  //     { label: "Manufacturing Plants", value: "48+" },
  //     { label: "Production Lines", value: "1K+" },
  //     { label: "Efficiency Improvement", value: "35%" },
  //     { label: "Cost Reduction", value: "40%" },
  //   ],
  // },
  logistics: {
    // icon: Truck,
    title: "Logistics",
    // category: "Logistics & Transportation",
    description:
      "Transform logistics through smart transportation, last-mile delivery, warehousing, EV-powered mobility, and AI-driven digital logistics platforms for modern enterprise distribution.",
    image: logisticsImg,

    sectionImages: {
      overview: logisticsOverviewImg,
      // challenges: logisticsChallengesImg,
      technologies: logisticsTechImg,
      benefits: logisticsBenefitsImg,
      caseStudies: logisticsCaseStudyImg,
    },

    overview:
      "City Link delivers advanced logistics and transportation technology solutions that simplify supply chain operations, optimize fleet management, modernize warehousing, and enable sustainable mobility. From intra-city and inter-city transportation to smart warehousing, EV logistics, and AI-powered digital platforms, we empower logistics businesses to improve operational efficiency, delivery speed, and supply chain visibility.",

    challenges: [
      "Complex fleet transportation and route planning",
      "Inefficient urban and last-mile delivery operations",
      "Limited real-time shipment visibility",
      "Warehouse and fulfillment inefficiencies",
      "High operational and fuel costs",
      "Sustainability and carbon reduction pressures",
      "Scalability for enterprise logistics ecosystems",
    ],

    solutions: [
      // Transportation
      // "Fleet Transportation Management",
      // "Dedicated Fleet Services",
      "Scheduled Transportation",
      "Route Optimization",
      // "Multi-city Operations",
      // "Intra-city Logistics Solutions",
      "Inter-city Transportation Systems",

      // Last Mile
      "Smart Last Mile Delivery Solutions",
      // "Same-day Delivery Systems",
      "Real-time Tracking",
      // "Delivery Proof Systems",
      // "Dispatch Automation",

      // Warehousing
      "Smart Warehouse Management",
      // "Inventory Management",
      // // "Order Fulfillment",
      // "Storage & Dispatch Operations",
      // "Digitized Warehouse Systems",

      // Sustainability
      "EV Logistics & Sustainable Mobility",
      // "Electric Fleet Management",
      // "Green Supply Chain Platforms",

      // Platform
      // "Digital Logistics Platform Development",
      "AI Route Planning",
      // "Logistics Analytics  Dashboards",
    ],

    technologies: [
      "Custom Development",
      "GPS Fleet Tracking",
      "AI-based Route Planning",
      "Real-time Shipment Visibility",
      "Automated Dispatch Systems",
      "Logistics Analytics Dashboard",
      "Mobile Fleet Applications",
      "Delivery Monitoring",
      "Digital Workflow Automation",
      "Predictive Logistics Analytics",
      "Fleet Performance Tracking",
      "Barcode Systems",
      "RFID",
      "IoT",
      "WMS",
      "TMS",
      "EV Fleet Systems",
      "Centralized Logistics Management",
    ],

    benefits: [
      "Improved transportation and logistics efficiency",
      "Faster intra-city and inter-city deliveries",
      "Enhanced last-mile distribution performance",
      "Reduced fuel and operational costs",
      "Greater supply chain visibility",
      "Digitized warehouse and fulfillment operations",
      "Sustainable EV-powered logistics transformation",
      "Scalable enterprise logistics ecosystems",
    ],

    caseStudies: [
      {
        title: "AI-Powered Urban Last Mile Delivery Platform",
        description:
          "Developed smart last-mile logistics ecosystem for urban enterprise distribution with AI routing and real-time tracking.",
        results: [
          "45% faster urban deliveries",
          "30% lower dispatch costs",
          "98% delivery visibility",
        ],
      },
      {
        title: "EV Logistics & Warehouse Modernization",
        description:
          "Built sustainable EV-powered logistics and warehouse automation platform for regional distribution enterprise.",
        results: [
          "35% operational cost reduction",
          "50% reduction in carbon footprint",
          "40% warehouse efficiency improvement",
        ],
      },
    ],

    stats: [
      { label: "Shipments Managed", value: "10M+" },
      { label: "Fleet Operations", value: "50K+" },
      { label: "Operational Cost Reduction", value: "35%" },
      { label: "Delivery Visibility", value: "99%" },
    ],
  },
  entertainment: {
    // icon: Clapperboard,
    title: "Entertainment",
    // category: "Entertainment Industry",
    description:
      "Create immersive entertainment experiences with digital platforms and content delivery solutions.",
    image: entertainmentImg,
    overview:
      "We create cutting-edge entertainment technology solutions that captivate audiences and drive engagement. Our platforms support content creation, distribution, and monetization across multiple channels and platforms.",
    challenges: [
      "Multi-platform content distribution",
      "Audience engagement and retention",
      "Content monetization optimization",
      "Digital rights management",
      "Scalable streaming infrastructure",
    ],
    solutions: [
      "Streaming Platform Development",
      "Content Management Systems",
      "Audience Engagement Platforms",
      "Digital Rights Management (DRM)",
      "Monetization and Analytics",
      "Social Integration Solutions",
    ],
    technologies: [
      "Custom Development",
      "Streaming Platforms",
      "DRM",
      "CDN",
      "Video Analytics",
      "Social Integration",
      "AI/ML",
      "Cloud Computing",
    ],
    benefits: [
      "Enhanced audience engagement",
      "Improved content monetization",
      "Scalable content delivery",
      "Better audience insights",
      "Increased revenue streams",
    ],
    caseStudies: [
      {
        title: "Global Entertainment Platform",
        description:
          "Built multi-platform entertainment ecosystem for major studio",
        results: [
          "100M+ global users",
          "50% increase in engagement",
          "300% growth in revenue",
        ],
      },
      {
        title: "Interactive Gaming Platform",
        description: "Developed social gaming platform with real-time features",
        results: [
          "10M+ active users",
          "80% user retention rate",
          "40% increase in in-app purchases",
        ],
      },
    ],
    stats: [
      { label: "Global Users", value: "100M+" },
      { label: "Content Hours", value: "1M+" },
      { label: "Platform Uptime", value: "99.9%" },
      { label: "Revenue Growth", value: "200%" },
    ],
  },
  "consumer-goods": {
    // icon: Package,
    title: "Consumer Goods and Services",
    // category: "Consumer Products & Services",
    description:
      "Transform consumer product businesses with digital solutions for brand engagement and service delivery.",
    image: consumerGoodsImg,
    overview:
      "We empower consumer goods and services companies to build stronger brand connections, optimize operations, and enhance customer experiences through innovative digital solutions. Our expertise spans product lifecycle management, e-commerce platforms, customer engagement, and supply chain optimization.",
    challenges: [
      "Brand differentiation in competitive markets",
      "Direct-to-consumer channel development",
      "Supply chain complexity and transparency",
      "Customer experience personalization",
      "Omnichannel retail integration",
      "Sustainability and regulatory compliance",
    ],
    solutions: [
      "Product Lifecycle Management (PLM) Systems",
      "Direct-to-Consumer E-commerce Platforms",
      "Brand Management and Marketing Automation",
      "Customer Experience Personalization",
      "Supply Chain Visibility and Optimization",
      "Sustainability Tracking and Reporting",
      "Retail Partnership Integration",
      "Customer Service and Support Platforms",
    ],
    technologies: [
      "Custom Development",
      "PLM Systems",
      "CRM Platforms",
      "E-commerce Solutions",
      "Marketing Automation",
      "Supply Chain Analytics",
      "IoT Sensors",
      "AI/ML",
      "Mobile Applications",
    ],
    benefits: [
      "Enhanced brand visibility and customer loyalty",
      "40% improvement in time-to-market for new products",
      "Increased direct-to-consumer sales by 60%",
      "Better supply chain transparency and efficiency",
      "Improved customer satisfaction and retention",
      "Reduced operational costs through automation",
    ],
    caseStudies: [
      {
        title: "Global Beauty Brand Digital Transformation",
        description:
          "Implemented comprehensive digital ecosystem for international beauty and personal care brand",
        results: [
          "300% increase in online sales",
          "50% improvement in customer engagement",
          "25% faster product launches",
        ],
      },
      {
        title: "Consumer Electronics PLM Platform",
        description:
          "Built integrated product lifecycle management system for electronics manufacturer",
        results: [
          "40% reduction in product development time",
          "60% improvement in quality control",
          "35% cost savings in operations",
        ],
      },
    ],
    stats: [
      { label: "Consumer Brands Served", value: "150+" },
      { label: "Products Launched", value: "10K+" },
      { label: "Customer Engagement Increase", value: "75%" },
      { label: "Supply Chain Efficiency", value: "45%" },
    ],
  },
  //   utilities: {
  //     // icon: Power,
  //     title: "Utilities",
  //     // category: "Utilities",
  //     description:
  //       "Modernize utility operations with smart infrastructure, billing systems, and customer service platforms.",
  //     image: utilitiesImg,
  //     overview:
  //       "We deliver comprehensive utility solutions that modernize operations, improve customer service, and optimize infrastructure management. Our expertise spans smart grid technologies, utility billing systems, customer information systems, and regulatory compliance solutions for electric, gas, water, and renewable energy providers.",
  //     challenges: [
  //       "Aging infrastructure requiring modernization",
  //       "Regulatory compliance and reporting requirements",
  //       "Customer service and billing complexity",
  //       "Grid reliability and outage management",
  //       "Integration of renewable energy sources",
  //       "Cybersecurity threats to critical infrastructure",
  //     ],
  //     solutions: [
  //       "Smart Grid Management and Analytics",
  //       "Advanced Utility Billing Systems",
  //       "Customer Information Systems (CIS)",
  //       "Outage Management Systems (OMS)",
  //       "Asset Management and Maintenance",
  //       "Regulatory Compliance and Reporting",
  //       "Mobile Workforce Management",
  //       "Customer Self-Service Portals",
  //     ],
  //     technologies: [
  //       "Smart Meters",
  //       "SCADA Systems",
  //       "GIS Mapping",
  //       "CIS Platforms",
  //       "Meter Data Management",
  //       "Grid Analytics",
  //       "IoT Sensors",
  //       "Mobile Applications",
  //     ],
  //     benefits: [
  //       "Improved grid reliability and efficiency",
  //       "Enhanced customer service and satisfaction",
  //       "Reduced operational costs by up to 30%",
  //       "Better regulatory compliance and reporting",
  //       "Faster outage detection and restoration",
  //       "Optimized asset management and maintenance",
  //     ],
  //     caseStudies: [
  //       {
  //         title: "Smart Grid Modernization Program",
  //         description:
  //           "Implemented comprehensive smart grid solution for regional utility serving 800K customers",
  //         results: [
  //           "45% reduction in outage duration",
  //           "30% improvement in grid efficiency",
  //           "25% decrease in operational costs",
  //         ],
  //       },
  //       {
  //         title: "Unified Customer Information System",
  //         description:
  //           "Deployed integrated CIS platform for multi-service utility company",
  //         results: [
  //           "60% improvement in customer service response",
  //           "40% reduction in billing errors",
  //           "50% faster new service connections",
  //         ],
  //       },
  //     ],
  //     stats: [
  //       { label: "Utility Companies Served", value: "25+" },
  //       { label: "Smart Meters Deployed", value: "800+" },
  //       { label: "Grid Reliability", value: "99.8%" },
  //       { label: "Customer Satisfaction", value: "4.5/5" },
  //     ],
  //   },
};

const IndustryDetail = () => {
  const { industryId } = useParams<{ industryId: string }>();

  // Force re-render when industryId changes
  useEffect(() => {
    // Scroll to top immediately and after a short delay to ensure it works
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [industryId]);

  if (!industryId || !industriesData[industryId]) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="container mx-auto px-4 py-20">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Industry Not Found</h1>
            <p className="text-muted-foreground mb-8">
              The requested industry page could not be found.
            </p>
            <Link to="/">
              {/* <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button> */}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const industry = industriesData[industryId];
  // const IconComponent = industry.icon;

  const SectionWithOptionalImage = ({
    title,
    content,
    image,
    reverse = false,
  }: {
    title: string;
    content: React.ReactNode;
    image?: string;
    reverse?: boolean;
  }) => {
    if (!image) {
      return (
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">{title}</h2>
          {content}
        </div>
      );
    }

    return (
      <div
        className={`grid lg:grid-cols-2 gap-12 items-center ${
          reverse ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        <div>
          <img
            src={image}
            alt={title}
            className="w-full h-[400px] rounded-2xl shadow-lg"
          />
        </div>

        <div>
          <h2 className="text-3xl font-bold mb-6">{title}</h2>
          {content}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={industry.image}
            alt={industry.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl">
            {/* <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link> */}

            {/* <Badge variant="secondary" className="mb-4">
              {industry.category}
            </Badge> */}

            <div className="flex items-center gap-4 mb-6">
              {/* <div className="p-3 bg-primary/20 rounded-lg backdrop-blur-sm">
                <IconComponent className="h-8 w-8 text-white" />
              </div> */}
              <h1 className="text-4xl lg:text-6xl font-bold text-white  mt-12">
                {industry.title}
              </h1>
            </div>

            <p className="text-xl text-white/90 mb-8 max-w-3xl">
              {industry.description}
            </p>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="py-12 bg-accent/5">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {industry.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div
                  className={`text-3xl font-bold mb-2 ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"}`}
                >
                  {stat.value}
                </div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Overview Section */}
      {/* <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Industry Overview</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {industry.overview}
            </p>
          </div>
        </div>
      </section> */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <SectionWithOptionalImage
            title="Industry Overview"
            image={industry.sectionImages?.overview}
            content={
              <p className="text-lg text-muted-foreground leading-relaxed">
                {industry.overview}
              </p>
            }
          />
        </div>
      </section>
      {/* Challenges & Solutions */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4 space-y-12">
          {industry.segments?.length ? (
            // ===============================
            // SEGMENTED INDUSTRIES
            // ===============================
            industry.segments.map((segment, segmentIndex) => (
              <div key={segmentIndex}>
                <h2 className="text-3xl font-bold mb-8 text-center">
                  {segment.title}
                </h2>

                <div className="grid lg:grid-cols-2 gap-12">
                  {/* Challenges */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-destructive" />
                        {segment.title} Challenges
                      </CardTitle>
                    </CardHeader>

                    <CardContent>
                      <ul className="space-y-3">
                        {segment.challenges.map((challenge, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-destructive rounded-full mt-2 flex-shrink-0" />
                            <span className="text-sm">{challenge}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Solutions */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle
                          className={`h-5 w-5 text-primary ${segmentIndex === 0 ? "text-primary" : "text-secondary"}`}
                        />
                        {segment.title} Solutions
                      </CardTitle>
                    </CardHeader>

                    <CardContent>
                      <ul className="space-y-3">
                        {segment.solutions.map((solution, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <CheckCircle
                              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${segmentIndex === 0 ? "text-primary" : "text-secondary"}`}
                            />
                            <span className="text-sm">{solution}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))
          ) : (
            // ===============================
            // SINGLE INDUSTRIES
            // ===============================
            <div className="grid lg:grid-cols-2 gap-12">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-destructive" />
                    Industry Challenges
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3">
                    {industry.challenges?.map((challenge, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-destructive rounded-full mt-2 flex-shrink-0" />
                        <span className="text-sm">{challenge}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    Our Solutions
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3">
                    {industry.solutions?.map((solution, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                        <span className="text-sm">{solution}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
      {/* Technologies */}
      {/* <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
              <Zap className="h-6 w-6 text-secondary" />
              Technologies We Use
            </h2>
            <div className="flex flex-wrap gap-2">
              {industry.technologies.map((tech, index) => (
                <Badge key={index} variant="secondary" className="text-sm py-1">
                  {tech}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section> */}
      {/* Benefits */}
      {/* <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-secondary" />
              Key Benefits
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {industry.benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-background rounded-lg"
                >
                  <CheckCircle
                    className={`w-5 h-5 mt-0.5 flex-shrink-0 ${index % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"}`}
                  />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> */}
      {/* Technologies */}
      <section className="py-16">
        <div className="container mx-auto px-4 space-y-10">
          {industry.segments?.length ? (
            // ===============================
            // SEGMENTED INDUSTRIES
            // ===============================
            industry.segments.map((segment, index) => (
              <div key={index}>
                <h2 className="text-2xl font-bold mb-4">
                  {segment.title} Technologies
                </h2>

                <div className="flex flex-wrap gap-2">
                  {segment.technologies.map((tech, techIndex) => (
                    <Badge
                      key={techIndex}
                      variant="secondary"
                      className="text-sm py-1"
                    >
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>
            ))
          ) : (
            // ===============================
            // SINGLE INDUSTRIES
            // ===============================
            <div>
              <h2 className="text-3xl font-bold mb-6">Technologies We Use</h2>

              <div className="flex flex-wrap gap-2">
                {industry.technologies?.map((tech, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="text-sm py-1"
                  >
                    {tech}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      {/* Benefits */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4 space-y-12">
          {industry.segments?.length ? (
            // ===============================
            // SEGMENTED INDUSTRIES
            // ===============================
            industry.segments.map((segment, index) => (
              <div key={index}>
                <h2 className="text-2xl font-bold mb-6">
                  {segment.title} Benefits
                </h2>

                <div className="grid md:grid-cols-2 gap-4">
                  {segment.benefits.map((benefit, benefitIndex) => (
                    <div
                      key={benefitIndex}
                      className="flex items-start gap-3 p-4 bg-background rounded-lg"
                    >
                      <CheckCircle
                        className={`w-5 h-5 mt-0.5 flex-shrink-0 ${benefitIndex % 2 === 0 ? "text-primary" : "text-secondary"}`}
                      />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            // ===============================
            // SINGLE INDUSTRIES
            // ===============================
            <div>
              <h2 className="text-3xl font-bold mb-8">Key Benefits</h2>

              <div className="grid md:grid-cols-2 gap-4">
                {industry.benefits?.map((benefit, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 bg-background rounded-lg"
                  >
                    <CheckCircle
                      className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        index % 2 === 0
                          ? "text-primary shadow-glow"
                          : "text-secondary shadow-glow-secondary"
                      }`}
                    />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      {/* Case Studies */}
      {/* <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 flex items-center gap-2">
              <Users className="h-6 w-6 text-primary" />
              Success Stories
            </h2>
            <div className="grid lg:grid-cols-2 gap-8">
              {industry.caseStudies.map((caseStudy, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-xl">{caseStudy.title}</CardTitle>
                    <CardDescription>{caseStudy.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-semibold mb-3">Key Results:</h4>
                    <ul className="space-y-2">
                      {caseStudy.results.map((result, resultIndex) => (
                        <li
                          key={resultIndex}
                          className="flex items-start gap-2"
                        >
                          <CheckCircle
                            className={`w-4 h-4 mt-0.5 flex-shrink-0 ${resultIndex % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"}`}
                          />
                          <span className="text-sm">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section> */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {industry.sectionImages?.caseStudies ? (
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div className="grid lg:grid-cols-1 gap-8">
                {industry.caseStudies.map((caseStudy, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <CardTitle className="text-xl">
                        {caseStudy.title}
                      </CardTitle>
                      <CardDescription>{caseStudy.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <h4 className="font-semibold mb-3">Key Results:</h4>
                      <ul className="space-y-2">
                        {caseStudy.results.map((result, resultIndex) => (
                          <li
                            key={resultIndex}
                            className="flex items-start gap-2"
                          >
                            <CheckCircle
                              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${resultIndex % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"}`}
                            />
                            <span className="text-sm">{result}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <img
                src={industry.sectionImages.caseStudies}
                alt="Case Studies"
                className="w-full h-full max-h-[700px] rounded-2xl shadow-lg"
              />
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8">
              {industry.caseStudies.map((caseStudy, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-xl">{caseStudy.title}</CardTitle>
                    <CardDescription>{caseStudy.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-semibold mb-3">Key Results:</h4>
                    <ul className="space-y-2">
                      {caseStudy.results.map((result, resultIndex) => (
                        <li
                          key={resultIndex}
                          className="flex items-start gap-2"
                        >
                          <CheckCircle
                            className={`w-4 h-4 mt-0.5 flex-shrink-0 ${resultIndex % 2 === 0 ? "text-primary shadow-glow" : "text-secondary shadow-glow-secondary"}`}
                          />
                          <span className="text-sm">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default IndustryDetail;
